import { useEffect, useMemo, useState } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { LogOut } from 'lucide-react'
import { useUserAuthStore } from '../../store/userAuthStore'
import { ApiDebuggerPanel } from '../user/ApiDebuggerPanel'

export function UserLayout() {
  const navigate = useNavigate()
  const { user, fetchProfile, logout } = useUserAuthStore()
  const [profileLoading, setProfileLoading] = useState(false)
  const [profileError, setProfileError] = useState<string | null>(null)

  const navItems = useMemo(
    () => [
      { to: '/user', label: 'Feed', end: true },
      { to: '/user/posts', label: 'Posts' },
      { to: '/user/trips', label: 'Trips' },
      { to: '/user/events', label: 'Events' },
      { to: '/user/explore', label: 'Explore' },
      { to: '/user/profile', label: 'Profile' },
    ],
    []
  )

  useEffect(() => {
    let isMounted = true

    const loadProfile = async () => {
      if (user || profileLoading) return
      setProfileLoading(true)
      setProfileError(null)
      try {
        await fetchProfile()
      } catch (error: any) {
        if (!isMounted) return
        const message =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          'Failed to load profile'
        setProfileError(message)
      } finally {
        if (isMounted) {
          setProfileLoading(false)
        }
      }
    }

    loadProfile()

    return () => {
      isMounted = false
    }
  }, [user, fetchProfile, profileLoading])

  const handleSignOut = () => {
    logout()
    navigate('/login?mode=user')
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Waybook API Playground</h1>
            <p className="text-sm text-gray-600">
              Explore the user-facing API with real data from api.waybook.me
            </p>
          </div>

          <div className="flex items-center gap-4">
            {profileLoading && (
              <span className="text-sm text-gray-500">Loading profile...</span>
            )}
            {profileError && (
              <span className="text-sm text-red-500">{profileError}</span>
            )}

            {user && (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <div className="h-10 w-10 rounded-full bg-gray-200 overflow-hidden">
                    {user.avatar_url ? (
                      <img
                        src={user.avatar_url}
                        alt={user.display_name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center text-sm text-gray-600">
                        {user.display_name.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="text-sm">
                    <div className="font-medium text-gray-900">{user.display_name}</div>
                    <div className="text-gray-500">{user.email}</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
                >
                  <LogOut className="h-4 w-4" />
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-8">
          <div>
            <nav className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl p-2 overflow-x-auto">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `flex-1 whitespace-nowrap text-center py-2 rounded-lg text-sm font-medium transition ${
                      isActive ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>

            <div className="mt-6">
              <Outlet />
            </div>
          </div>

          <ApiDebuggerPanel />
        </div>
      </main>
    </div>
  )
}
